import { Controller } from "@hotwired/stimulus"
import { DateTime } from "luxon"
import titleize from "titleize"
import { columnPersistenceOptions, columnVisibilityHeaderMenu, emptyStatePlaceholder } from "./tabulator_helpers"

// The document list (/paperwork) as a Tabulator grid. The page runs a search
// once for its summary; this grid's URL carries that search's plan token, so
// its JSON rows replay the stored plan and never call the model again.
export default class extends Controller {
  static targets = ["table"]

  static values = {
    url: String,
    empty: String,
  }

  calculateTableHeight() {
    const visibleHeight = (element) =>
      element && window.getComputedStyle(element).display !== "none" ? element.offsetHeight : 0
    const header = visibleHeight(document.querySelector(".section-header-slim, .section-header"))
    const mobileHeader = visibleHeight(document.querySelector("header.mobile"))
    // Both the filter chips and the search summary are filter rows.
    const filters = Array.from(document.querySelectorAll(".section-filters"))
      .reduce((sum, element) => sum + Math.max(visibleHeight(element) - 6, 0), 0)
    return `max(240px, calc(100vh - ${mobileHeader + header + filters}px))`
  }

  // A cell value that filters the list to itself, keeping the current search.
  filterLink(param, value, text) {
    const url = new URL(window.location.href)
    url.searchParams.delete("page")
    url.searchParams.set(param, value)
    const link = document.createElement("a")
    link.href = url.toString()
    link.className = "link"
    link.setAttribute("data-turbo-prefetch", "false")
    link.textContent = text
    return link
  }

  connect() {
    const documentUrl = (data) => `/paperwork/${encodeURIComponent(data.reference)}`

    this.tabulatorTable = new Tabulator(this.tableTarget, {
      layout: "fitDataFill",
      ...columnPersistenceOptions("documents-table"),
      index: "reference",
      columnDefaults: {
        minWidth: 90,
        headerMenu: columnVisibilityHeaderMenu,
      },
      placeholder: emptyStatePlaceholder(this.emptyValue || "No paperwork yet"),
      pagination: true,
      paginationMode: "remote",
      paginationCounter: "rows",
      paginationSize: 100,
      paginationSizeSelector: [100, 250, 500, 1000],
      sortMode: "remote",
      height: this.calculateTableHeight(),
      renderHorizontal: "virtual",
      dataSendParams: {
        page: "page",
        size: "length",
        sorters: "sort",
      },
      ajaxURL: this.urlValue,
      ajaxResponse: (url, params, response) => ({
        data: response.documents,
        last_page: response.last_page,
        last_row: response.last_row,
      }),
      columns: [
        {
          title: "Reference",
          field: "reference",
          width: 130,
          minWidth: 110,
          formatter: "link",
          formatterParams: { url: (cell) => documentUrl(cell.getRow().getData()) },
        },
        {
          title: "Title",
          field: "title",
          minWidth: 240,
          widthGrow: 2,
          formatter: "link",
          formatterParams: { url: (cell) => documentUrl(cell.getRow().getData()) },
          cssClass: "font-bold",
        },
        {
          title: "Type",
          field: "type",
          width: 150,
          minWidth: 110,
          formatter: (cell) => {
            const data = cell.getRow().getData()
            return this.filterLink("type", data.type_filter, data.type_label || "")
          },
          accessorDownload: (value, data) => data.type_label,
        },
        {
          title: "Status",
          field: "status",
          width: 125,
          minWidth: 100,
          // Plain text, as on the workflow list. Colour would repeat the word.
          formatter: (cell) => {
            const data = cell.getRow().getData()
            if (!data.status) return ""
            return this.filterLink(data.status_param, data.status, titleize(String(data.status).replace(/_/g, " ")))
          },
        },
        {
          title: "Workflow",
          field: "process_reference",
          width: 130,
          minWidth: 110,
          headerSort: false,
          formatter: (cell) => {
            const reference = cell.getValue()
            if (!reference) return ""
            const link = document.createElement("a")
            link.href = `/processes/${encodeURIComponent(reference)}`
            link.className = "link"
            link.setAttribute("data-turbo-prefetch", "false")
            link.textContent = reference
            return link
          },
        },
        {
          title: "Created",
          field: "created_at",
          width: 170,
          minWidth: 150,
          formatter: "datetime",
          formatterParams: {
            inputFormat: "iso",
            outputFormat: "MMM d, yy 'at' t",
            invalidPlaceholder: "",
            timezone: "America/Los_Angeles",
          },
        },
      ],
      dependencies: {
        DateTime: DateTime,
      },
    })

    this.tabulatorTable.on("rowDblClick", (e, row) => {
      window.location.href = documentUrl(row.getData())
    })

    this.downloadButton = document.getElementById("download_csv")
    this.downloadCsv = () => {
      this.tabulatorTable.download("csv", `documents-${DateTime.now().toFormat("yyyy-MM-dd-HH-mm")}.csv`)
    }
    this.downloadButton?.addEventListener("click", this.downloadCsv)
  }

  disconnect() {
    this.downloadButton?.removeEventListener("click", this.downloadCsv)
    this.tabulatorTable?.destroy()
  }
}
